import { Button } from '@components'
import { useOpenLucaWithPrompt } from '@hooks'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaRegSquareCheck } from 'react-icons/fa6'
import { FaArrowRight, FaPen } from 'react-icons/fa'
import {
  AttentionDot,
  Eyebrow,
  UncertaintyCheckItem,
  UncertaintyDocHint,
  UncertaintyHoverActions,
  UncertaintyHoverBody,
  UncertaintyHoverCard as UncertaintyHoverCardSurface,
  UncertaintyHoverHeading,
  UncertaintyHoverTitleRow,
  UncertaintyLucaAsk,
  UncertaintyLucaAskRow,
  UncertaintyLucaAskTitle,
  UncertaintyPanelCard,
  UncertaintyWhy
} from '../styles'
import { UncertaintyCode } from './model'
import { useSuggestionUncertainty } from './SuggestionUncertaintyContext'

interface UncertaintyHoverCardProps {
  code: UncertaintyCode
  align?: 'start' | 'end'
  promptContext?: Record<string, string>
}

export const UncertaintyHoverCard: React.FC<UncertaintyHoverCardProps> = ({
  code,
  align = 'start',
  promptContext
}) => {
  const { t } = useTranslation()
  const openLucaWithPrompt = useOpenLucaWithPrompt()
  const { dismiss, requestEdit, focusAttachments, isOpen } = useSuggestionUncertainty()
  const rawChecks = t(`document.suggestionCard.review.items.${code}.checks`, {
    returnObjects: true
  })
  const checks = Array.isArray(rawChecks) ? rawChecks : []
  const rawPrompts = t(`document.suggestionCard.review.items.${code}.ask`, {
    returnObjects: true,
    ...promptContext
  })
  const prompts = Array.isArray(rawPrompts)
    ? rawPrompts.filter(prompt => typeof prompt === 'string')
    : []
  const showDocHint = code !== 'missing_attachment' && isOpen('missing_attachment')
  const canEdit = code !== 'missing_attachment'

  const confirm = () => {
    if (code === 'missing_attachment') {
      focusAttachments()
      return
    }
    dismiss(code)
  }

  const askLuca = (prompt: string) => {
    openLucaWithPrompt(prompt)
  }

  return (
    <UncertaintyHoverCardSurface className="uncertainty-hover-card" $align={align}>
      <UncertaintyPanelCard>
        <UncertaintyHoverBody>
          <UncertaintyHoverTitleRow>
            <AttentionDot $pulse={false} />
            <UncertaintyHoverHeading>
              {t(`document.suggestionCard.review.items.${code}.title`)}
            </UncertaintyHoverHeading>
          </UncertaintyHoverTitleRow>
          <div>
            <Eyebrow>{t('document.suggestionCard.review.checkHeading')}</Eyebrow>
            {checks.map(check => (
              <UncertaintyCheckItem key={check}>
                <FaRegSquareCheck aria-hidden />
                <span>{check}</span>
              </UncertaintyCheckItem>
            ))}
          </div>
          <div>
            <Eyebrow>{t('document.suggestionCard.review.whyHeading')}</Eyebrow>
            <UncertaintyWhy>{t(`document.suggestionCard.review.items.${code}.why`)}</UncertaintyWhy>
          </div>
        </UncertaintyHoverBody>
        {showDocHint && (
          <UncertaintyDocHint>
            {t('document.suggestionCard.review.docHintTooltip')}
          </UncertaintyDocHint>
        )}
        {prompts.length > 0 && (
          <UncertaintyLucaAsk>
            <UncertaintyLucaAskTitle>
              {t('document.suggestionCard.review.askTitle')}
            </UncertaintyLucaAskTitle>
            {prompts.map(prompt => (
              <UncertaintyLucaAskRow
                key={prompt}
                type="button"
                onClick={event => {
                  event.preventDefault()
                  event.stopPropagation()
                  askLuca(prompt)
                }}
              >
                <span>{prompt}</span>
                <FaArrowRight aria-hidden />
              </UncertaintyLucaAskRow>
            ))}
          </UncertaintyLucaAsk>
        )}
        <UncertaintyHoverActions>
          {canEdit && (
            <Button
              type="button"
              size="sm"
              isSecondary
              icon={<FaPen />}
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                requestEdit()
              }}
            >
              {t(`document.suggestionCard.review.items.${code}.edit`)}
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={event => {
              event.preventDefault()
              event.stopPropagation()
              confirm()
            }}
          >
            {t(`document.suggestionCard.review.items.${code}.confirm`)}
          </Button>
        </UncertaintyHoverActions>
      </UncertaintyPanelCard>
    </UncertaintyHoverCardSurface>
  )
}
